import { useState, useCallback } from 'react';
import { 
  Search, MapPin, Star, Download, CheckCircle, 
  AlertTriangle, Globe, Building2, RefreshCw, Filter,
  ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'alloggi', label: 'Alloggi', icon: '🏨', desc: 'Hotel, B&B, Agriturismi' },
  { value: 'ristorazione', label: 'Ristorazione', icon: '🍽️', desc: 'Ristoranti, Trattorie' },
  { value: 'cantine', label: 'Cantine', icon: '🍷', desc: 'Cantine, Enoteche' },
  { value: 'attrazioni', label: 'Attrazioni', icon: '🏛️', desc: 'Musei, Monumenti' },
  { value: 'esperienze', label: 'Esperienze', icon: '🚴', desc: 'Tour, Noleggi' },
  { value: 'benessere', label: 'Benessere', icon: '💆', desc: 'Spa, Terme' },
];

const TYPE_MAPPING = {
  alloggi: 'accommodation',
  ristorazione: 'restaurant',
  cantine: 'winery',
  attrazioni: 'museum',
  esperienze: 'experience',
  benessere: 'wellness',
};

const SUBTYPE_MAPPING = {
  alloggi: 'hotel',
  ristorazione: 'ristorante',
  cantine: 'cantina',
  attrazioni: 'museo',
  esperienze: 'tour',
  benessere: 'spa',
};

export function GooglePlacesPanel() {
  const [category, setCategory] = useState('alloggi');
  const [customZones, setCustomZones] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [showImported, setShowImported] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState(null);

  const searchPlaces = useCallback(async () => {
    setLoading(true);
    setResults(null);
    setSelectedIds(new Set());
    
    try {
      const params = new URLSearchParams();
      params.append('category', category);
      
      if (customZones.trim()) {
        customZones.split(',').forEach(zone => {
          params.append('zones', zone.trim());
        });
      }
      
      const res = await fetch(`${API}/admin/places/search-smart?${params}`, {
        method: 'POST',
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ricerca fallita');
      }
      
      const data = await res.json();
      setResults(data);
      toast.success(`Trovati ${data.total_found} risultati (${data.new_to_import} nuovi)`);
    } catch (err) {
      console.error('Search failed:', err);
      toast.error(err.message || 'Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  }, [category, customZones]);

  const toggleSelection = (placeId) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(placeId)) {
      newSet.delete(placeId);
    } else {
      newSet.add(placeId);
    }
    setSelectedIds(newSet);
  };

  const selectAllNew = () => {
    if (!results?.results) return;
    const newIds = results.results
      .filter(r => !r.already_imported)
      .map(r => r.place_id);
    setSelectedIds(new Set(newIds));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const importSelected = async () => {
    if (selectedIds.size === 0) {
      toast.error('Seleziona almeno un luogo da importare');
      return;
    }

    setImporting(true);
    try {
      const res = await fetch(`${API}/admin/places/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_ids: Array.from(selectedIds),
          default_type: TYPE_MAPPING[category] || 'other',
          default_subtype: SUBTYPE_MAPPING[category] || 'generic'
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Import fallito');
      }

      const data = await res.json();
      toast.success(`Importati ${data.imported?.length || 0} provider con successo`);
      
      // Refresh search to update imported status
      await searchPlaces();
    } catch (err) {
      console.error('Import failed:', err);
      toast.error(err.message || 'Errore durante l\'import');
    } finally {
      setImporting(false);
    }
  };

  const filteredResults = results?.results?.filter(r => 
    showImported || !r.already_imported
  ) || [];

  return (
    <div className="space-y-6" data-testid="google-places-panel">
      {/* Search Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" />
            Ricerca Google Places
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Category Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Categoria
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setCategory(cat.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    category === cat.value
                      ? 'border-umbria-olive bg-umbria-olive/10'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`category-${cat.value}`}
                >
                  <div className="text-xl mb-1">{cat.icon}</div>
                  <div className="text-sm font-medium">{cat.label}</div>
                  <div className="text-xs text-gray-500">{cat.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Zones */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Zone personalizzate (opzionale)
            </label>
            <Input
              placeholder="Es: Montefalco, Bevagna, Trevi (lascia vuoto per tutte)"
              value={customZones}
              onChange={(e) => setCustomZones(e.target.value)}
              data-testid="custom-zones-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Se vuoto, cerca in tutte le principali località umbre
            </p>
          </div>

          {/* Search Button */}
          <div className="flex gap-3">
            <Button 
              onClick={searchPlaces} 
              disabled={loading}
              className="gap-2"
              data-testid="search-places-btn"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              {loading ? 'Ricerca in corso...' : 'Cerca su Google Places'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Risultati Ricerca
              </CardTitle>
              
              <div className="flex items-center gap-4">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">
                    Totali: <strong>{results.total_found}</strong>
                  </span>
                  <span className="text-green-600">
                    Nuovi: <strong>{results.new_to_import}</strong>
                  </span>
                  <span className="text-amber-600">
                    Già importati: <strong>{results.already_imported}</strong>
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Action Bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={showImported}
                    onCheckedChange={setShowImported}
                  />
                  Mostra già importati
                </label>
                
                <span className="text-sm text-gray-500">
                  Selezionati: <strong>{selectedIds.size}</strong>
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={selectAllNew}
                  disabled={results.new_to_import === 0}
                >
                  Seleziona tutti nuovi
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearSelection}
                  disabled={selectedIds.size === 0}
                >
                  Deseleziona
                </Button>
                <Button
                  onClick={importSelected}
                  disabled={selectedIds.size === 0 || importing}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="import-selected-btn"
                >
                  {importing ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {importing ? 'Importazione...' : `Importa (${selectedIds.size})`}
                </Button>
              </div>
            </div>

            {/* Results List */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {showImported ? 'Nessun risultato' : 'Tutti i risultati sono già importati'}
                </div>
              ) : (
                filteredResults.map((place) => (
                  <div
                    key={place.place_id}
                    className={`p-4 rounded-lg border transition-all ${
                      place.already_imported 
                        ? 'bg-gray-50 border-gray-200 opacity-70' 
                        : selectedIds.has(place.place_id)
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200 hover:border-umbria-olive'
                    }`}
                    data-testid={`place-card-${place.place_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <Checkbox
                        checked={selectedIds.has(place.place_id)}
                        onCheckedChange={() => toggleSelection(place.place_id)}
                        disabled={place.already_imported}
                        className="mt-1"
                      />
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className="font-medium text-umbria-charcoal">
                              {place.name}
                            </h4>
                            <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3" />
                              {place.address}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {place.already_imported && (
                              <Badge className="bg-amber-100 text-amber-700">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Già importato
                              </Badge>
                            )}
                            {place.rating && (
                              <Badge variant="secondary" className="gap-1">
                                <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                                {place.rating} ({place.user_ratings_total || 0})
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {/* Expandable details */}
                        <button
                          className="text-xs text-umbria-olive flex items-center gap-1 mt-2"
                          onClick={() => setExpandedDetails(
                            expandedDetails === place.place_id ? null : place.place_id
                          )}
                        >
                          {expandedDetails === place.place_id ? (
                            <ChevronUp className="h-3 w-3" />
                          ) : (
                            <ChevronDown className="h-3 w-3" />
                          )}
                          Dettagli
                        </button>
                        
                        {expandedDetails === place.place_id && (
                          <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1">
                            <p><strong>Zona:</strong> {place.found_in_zone}</p>
                            <p><strong>Query:</strong> {place.matched_query}</p>
                            <p><strong>Tipi Google:</strong> {place.types?.slice(0, 5).join(', ')}</p>
                            <p><strong>Place ID:</strong> <code className="bg-gray-200 px-1 rounded">{place.place_id}</code></p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Search Info */}
            {results.errors?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {results.errors.length} errori durante la ricerca
                  </span>
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500 text-center">
              Ricerche effettuate: {results.total_searches} | 
              Query usate: {results.queries_used?.join(', ')}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
